import {
    Box,
    VStack,
    Text,
    Icon,
    Flex,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ModalCloseButton,
    useToast,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    InputGroup,
    InputLeftElement,
    Input,
    InputRightElement,
    IconButton,
    Badge,
    Alert,
    AlertIcon,
    AlertDescription,
    HStack,
    Divider,
    FormControl,
    FormLabel,
} from '@chakra-ui/react';
import { useState, useMemo, useEffect, useRef, useCallback, useContext, type FC } from 'react';
import { TbSearch } from 'react-icons/tb';
import { FaEye, FaEyeSlash, FaSave } from 'react-icons/fa';
import SortArrowIcon, { SortIconDirection } from '../SortArrowIcon';
import { LocationEntry } from '../../hooks/useGetSettings';
import { RefetchOptions, QueryObserverResult } from '@tanstack/react-query';
import { Button, Icon as YaxleIcon } from '../../ui';
import Pagination from '../Pagination';
import { useResyncPlatformLocations } from '../../hooks/useResyncPlatformLocations';
import { useReconcileLocations, type PartstraderLocation, type CccLocation, type LocationMapping } from '../../hooks/useReconcileLocations';
import { useAddLocationsFromMapping } from '../../hooks/useAddLocationsFromMapping';
import { LocationMappingUI, type CccRowMapping } from '../onboarding/LocationMappingUI';
import { SupplierNetworkStep } from '../onboarding/SupplierNetworkStep';
import { AgenticStepLoader } from '../AgenticStepLoader';
import SourceBadge from '../SourceBadge';
import { useCreateSingleLocation } from '../../hooks/useCreateSingleLocation';
import axios from 'axios';
import SessionContext from '../../contexts/SessionContext';
import API_URL from '../../constants/environment';
import {
    SettingsEmptyState,
    SettingsPageActions,
    SettingsTable,
    SettingsTableFooter,
    SettingsTd,
    SettingsTh,
    useDynamicPageSize,
} from './primitives';
import { SETTINGS_TABLE_GUTTER } from './primitives/constants';
import { canAddOrganizationLocation } from '../../utils/yaxleAdminAccess';

const PARTBAY_BLUE = '#0071E3';

export interface LocationsProps {
    locations: Array<LocationEntry>;
    refetch: (options?: RefetchOptions) => Promise<QueryObserverResult<any, Error>>;
    setupType?: 'single-server' | 'multi-server' | 'no-server' | null;
}

interface NewLocation {
    _id: string;
    title: string;
}

interface CredentialRow {
    locationId: string;
    locationTitle: string;
    mitchellUsername: string;
    mitchellPassword: string;
    opsUsername: string;
    opsPassword: string;
}

interface ManualLocationForm {
    title: string;
    address: string;
    city: string;
    state: string;
    zip: string;
}

const EMPTY_MANUAL_LOCATION_FORM: ManualLocationForm = {
    title: '',
    address: '',
    city: '',
    state: '',
    zip: '',
};

type ModalStep = 'manual' | 'syncing' | 'mapping' | 'credentials' | 'supplier-mapping' | 'server-setup';

const Locations: FC<LocationsProps> = ({ locations, refetch, setupType }) => {
    const sessionContext = useContext(SessionContext);
    const canAddLocation = canAddOrganizationLocation(sessionContext);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalStep, setModalStep] = useState<ModalStep>('syncing');
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [nameSortDirection, setNameSortDirection] = useState<'asc' | 'desc' | null>(null);
    const { scrollRef: tableScrollRef, pageSize: locationsPerPage } = useDynamicPageSize();
    const [ptLocations, setPtLocations] = useState<PartstraderLocation[]>([]);
    const [cccLocations, setCccLocations] = useState<CccLocation[]>([]);
    const [suggestedMappings, setSuggestedMappings] = useState<LocationMapping[]>([]);
    const [cccMappings, setCccMappings] = useState<Record<string, CccRowMapping>>({});

    const [newLocations, setNewLocations] = useState<NewLocation[]>([]);
    const [credRows, setCredRows] = useState<CredentialRow[]>([]);
    const [showPasswords, setShowPasswords] = useState<Record<string, boolean>>({});
    const [savingCreds, setSavingCreds] = useState(false);
    const [generatingAccounts, setGeneratingAccounts] = useState(false);

    const resync = useResyncPlatformLocations();
    const reconcile = useReconcileLocations();
    const addFromMapping = useAddLocationsFromMapping();
    const createSingleLocation = useCreateSingleLocation();
    const toast = useToast();
    const hasReconciled = useRef(false);

    const [manualLocationForm, setManualLocationForm] = useState<ManualLocationForm>(EMPTY_MANUAL_LOCATION_FORM);

    const headers = { Authorization: sessionContext?.sessionId ?? '' };

    const filteredLocations = useMemo(() => {
        let result = locations;
        if (searchTerm) {
            result = result.filter(location =>
                location.title.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }
        if (nameSortDirection) {
            result = [...result].sort((a, b) => {
                const comparison = a.title.localeCompare(b.title);
                return nameSortDirection === 'asc' ? comparison : -comparison;
            });
        }
        return result;
    }, [locations, searchTerm, nameSortDirection]);

    const totalPages = useMemo(() => Math.ceil(filteredLocations.length / locationsPerPage), [filteredLocations.length, locationsPerPage]);

    // The page size follows the viewport, so a resize can shrink the page
    // count; clamp so the user is never stranded on a now-empty page.
    useEffect(() => {
        setCurrentPage((prev) => Math.min(prev, Math.max(1, totalPages)));
    }, [totalPages]);

    const currentLocations = useMemo(() => {
        const startIndex = (currentPage - 1) * locationsPerPage;
        return filteredLocations.slice(startIndex, startIndex + locationsPerPage);
    }, [filteredLocations, currentPage, locationsPerPage]);

    const handleSearchChange = (value: string) => {
        setSearchTerm(value);
        setCurrentPage(1);
    };

    const handleNameSortToggle = () => {
        setNameSortDirection(prev => {
            if (prev === null) return 'asc';
            if (prev === 'asc') return 'desc';
            return null;
        });
        setCurrentPage(1);
    };

    const getNameSortDirection = (): SortIconDirection => {
        if (nameSortDirection === 'asc') return 'asc';
        if (nameSortDirection === 'desc') return 'desc';
        return 'none';
    };

    const handleOpenAddModal = () => {
        hasReconciled.current = false;
        setPtLocations([]);
        setCccLocations([]);
        setSuggestedMappings([]);
        setCccMappings({});
        setNewLocations([]);
        setCredRows([]);
        setShowPasswords({});
        setManualLocationForm(EMPTY_MANUAL_LOCATION_FORM);
        // Platform sync is disabled for this entry point; all orgs add locations manually.
        setModalStep('manual');
        setIsModalOpen(true);
        resync.reset();
    };

    const handleManualLocationChange = (field: keyof ManualLocationForm, value: string) => {
        setManualLocationForm(prev => ({ ...prev, [field]: value }));
    };

    const handleSubmitManualLocation = async () => {
        const title = manualLocationForm.title.trim();
        const address = manualLocationForm.address.trim();
        const city = manualLocationForm.city.trim();
        const state = manualLocationForm.state.trim();
        const zip = manualLocationForm.zip.trim();

        if (!address || !city || !state || !zip) {
            toast({
                title: 'Missing location details',
                description: 'Address, city, state, and ZIP are required.',
                status: 'warning',
                duration: 4000,
                isClosable: true,
            });
            return;
        }

        try {
            await createSingleLocation.mutateAsync({
                ...(title ? { title } : {}),
                address,
                city,
                state,
                zip,
            });
            toast({
                title: 'Location added',
                description: title || [address, city, state].join(', '),
                status: 'success',
                duration: 4000,
                isClosable: true,
            });
            setManualLocationForm(EMPTY_MANUAL_LOCATION_FORM);
            handleFinish();
        } catch {
            toast({
                title: 'Failed to add location',
                description: 'Please check the location details and try again.',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        resync.reset();
    };

    const handleFinish = () => {
        handleCloseModal();
        refetch();
    };

    useEffect(() => {
        // Advance to mapping the moment the worker has persisted location lists, even if
        // the PT supplier crawl is still finishing in the background. Suppliers are needed
        // later for the supplier-mapping step, but we shouldn't block location mapping on them.
        if (!resync.locationsReady || hasReconciled.current) return;
        hasReconciled.current = true;

        const claimedPtSet = new Set(resync.claimedPtIds);
        const claimedCccSet = new Set(resync.claimedCccIds);

        const newPt = (resync.ptLocations ?? []).filter(pt => !claimedPtSet.has(pt.id));
        const newCcc = (resync.cccLocations ?? []).filter(ccc => !claimedCccSet.has(ccc.id));

        setPtLocations(newPt);
        setCccLocations(newCcc);

        if (newPt.length === 0 && newCcc.length === 0) {
            toast({
                title: 'No new locations',
                description: 'All locations from PartsTrader and CCC are already mapped.',
                status: 'info',
                duration: 5000,
                isClosable: true,
            });
            handleCloseModal();
            return;
        }

        toast({
            title: 'Locations synced',
            description: `Found ${newPt.length} new PartsTrader and ${newCcc.length} new CCC locations.`,
            status: 'success',
            duration: 5000,
            isClosable: true,
        });

        (async () => {
            let reconciledMappings: LocationMapping[] = [];
            try {
                const result = await reconcile.mutateAsync({
                    partstraderLocations: newPt,
                    cccLocations: newCcc,
                });
                reconciledMappings = result.mappings;
                setSuggestedMappings(result.mappings);
            } catch { /* reconcile is best-effort */ }

            const initial: Record<string, CccRowMapping> = {};
            newCcc.forEach(ccc => { initial[ccc.id] = { enabled: true, ptIds: [] }; });
            reconciledMappings.forEach(m => {
                if (initial[m.cccId]) {
                    initial[m.cccId].ptIds.push(m.partstraderId);
                }
            });
            setCccMappings(initial);
            setModalStep('mapping');
        })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resync.locationsReady]);

    const handleToggleCcc = useCallback((cccId: string) => {
        setCccMappings(prev => ({
            ...prev,
            [cccId]: { ...prev[cccId], enabled: !prev[cccId]?.enabled },
        }));
    }, []);

    const handleAddPt = useCallback((cccId: string, ptId: string) => {
        setCccMappings(prev => ({
            ...prev,
            [cccId]: { ...prev[cccId], ptIds: [...(prev[cccId]?.ptIds ?? []), ptId] },
        }));
    }, []);

    const handleRemovePt = useCallback((cccId: string, ptId: string) => {
        setCccMappings(prev => ({
            ...prev,
            [cccId]: { ...prev[cccId], ptIds: (prev[cccId]?.ptIds ?? []).filter(id => id !== ptId) },
        }));
    }, []);

    const handleConfirmMapping = async (confirmed: LocationMapping[]) => {
        if (confirmed.length === 0) {
            toast({
                title: 'No mappings',
                description: 'Please map at least one location.',
                status: 'warning',
                duration: 3000,
                isClosable: true,
            });
            return;
        }
        try {
            const cccMap = new Map(cccLocations.map(c => [c.id, c]));
            const result = await addFromMapping.mutateAsync(
                confirmed.map(m => {
                    const ccc = cccMap.get(m.cccId);
                    return {
                        partstraderId: m.partstraderId,
                        cccId: m.cccId,
                        title: ccc?.name,
                        address: ccc?.address,
                        city: ccc?.city,
                        state: ccc?.state,
                        zip: ccc?.zip,
                    };
                }),
            );
            toast({
                title: 'Locations added',
                description: `${result.locations.length} location${result.locations.length !== 1 ? 's' : ''} created.`,
                status: 'success',
                duration: 5000,
                isClosable: true,
            });

            const created: NewLocation[] = result.locations;
            setNewLocations(created);
            setCredRows(
                created.map((loc: NewLocation) => ({
                    locationId: loc._id,
                    locationTitle: loc.title,
                    mitchellUsername: '',
                    mitchellPassword: '',
                    opsUsername: '',
                    opsPassword: '',
                }))
            );
            setModalStep('credentials');
        } catch (err: unknown) {
            toast({
                title: 'Failed to create locations',
                description: err instanceof Error ? err.message : 'Could not create locations.',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    };

    const togglePassword = (key: string) =>
        setShowPasswords(prev => ({ ...prev, [key]: !prev[key] }));

    const updateCredRow = (idx: number, field: keyof CredentialRow, value: string) => {
        setCredRows(prev => {
            const next = [...prev];
            next[idx] = { ...next[idx], [field]: value };
            return next;
        });
    };

    const handleSaveCredentials = async () => {
        const mitchellCreds = credRows
            .filter(r => r.mitchellUsername.trim())
            .map(r => ({
                locationId: r.locationId,
                username: r.mitchellUsername,
                ...(r.mitchellPassword ? { password: r.mitchellPassword } : {}),
            }));

        const opsCreds = credRows
            .filter(r => r.opsUsername.trim())
            .map(r => ({
                locationId: r.locationId,
                username: r.opsUsername,
                ...(r.opsPassword ? { password: r.opsPassword } : {}),
            }));

        if (mitchellCreds.length === 0 && opsCreds.length === 0) {
            advanceFromCredentials();
            return;
        }

        setSavingCreds(true);
        try {
            const requests: Promise<unknown>[] = [];
            if (mitchellCreds.length > 0) {
                requests.push(
                    axios.post(
                        `${API_URL}/settings/account/integrations`,
                        { action: 'upsert', provider: 'mitchell', credentials: mitchellCreds },
                        { headers }
                    )
                );
            }
            if (opsCreds.length > 0) {
                requests.push(
                    axios.post(
                        `${API_URL}/settings/account/integrations`,
                        { action: 'upsert', provider: 'opstrax', credentials: opsCreds },
                        { headers }
                    )
                );
            }
            await Promise.all(requests);

            const parts: string[] = [];
            if (mitchellCreds.length > 0) parts.push(`${mitchellCreds.length} Mitchell`);
            if (opsCreds.length > 0) parts.push(`${opsCreds.length} OPSTrax`);
            toast({
                title: 'Credentials saved',
                description: `${parts.join(' and ')} credential${mitchellCreds.length + opsCreds.length > 1 ? 's' : ''} saved.`,
                status: 'success',
                duration: 4000,
                isClosable: true,
            });
            advanceFromCredentials();
        } catch {
            toast({
                title: 'Failed to save credentials',
                status: 'error',
                duration: 4000,
                isClosable: true,
            });
        } finally {
            setSavingCreds(false);
        }
    };

    const advanceFromCredentials = () => {
        // Always pause on the supplier-mapping step so the user can review/confirm any new
        // PartsTrader (and OPSTrax, if creds were entered) suppliers that were discovered alongside the
        // location pull. From there we either continue to server-setup (multi-server orgs)
        // or close the modal.
        setModalStep('supplier-mapping');
    };

    const advanceFromSupplierMapping = () => {
        if (setupType === 'multi-server') {
            setModalStep('server-setup');
        } else {
            handleFinish();
        }
    };

    const handleGenerateServerAccounts = async () => {
        setGeneratingAccounts(true);
        try {
            await axios.post(
                `${API_URL}/settings/account/it-setup`,
                { action: 'generate-server-accounts', setupType: 'multi-server' },
                { headers }
            );
            toast({
                title: 'Server accounts created',
                description: `Server accounts generated for ${newLocations.length} new location${newLocations.length !== 1 ? 's' : ''}.`,
                status: 'success',
                duration: 5000,
                isClosable: true,
            });
            handleFinish();
        } catch {
            toast({
                title: 'Failed to generate accounts',
                description: 'Could not create server accounts. You can generate them later in IT Setup.',
                status: 'error',
                duration: 4000,
                isClosable: true,
            });
        } finally {
            setGeneratingAccounts(false);
        }
    };

    const syncAgenticSteps = [
        { label: 'Starting sync', description: 'Initiating location pull' },
        { label: 'Pulling from PartsTrader', description: 'Fetching PartsTrader locations' },
        { label: 'Pulling from CCC', description: 'Fetching CCC locations' },
        { label: 'Reconciling locations', description: 'Filtering out existing locations' },
    ];

    const getSyncCurrentStep = (): number => {
        if (resync.resync.isPending) return 0;
        if (resync.isPolling) {
            const ptDone = resync.ptLocations !== null;
            const cccDone = resync.cccLocations !== null;
            if (!ptDone) return 1;
            if (!cccDone) return 2;
        }
        if (reconcile.isPending) return 3;
        return 1;
    };

    const getModalSize = () => {
        if (modalStep === 'mapping') return '5xl';
        if (modalStep === 'credentials') return '5xl';
        if (modalStep === 'supplier-mapping') return '5xl';
        return 'md';
    };

    const getModalTitle = () => {
        switch (modalStep) {
            case 'manual': return 'Add Location';
            case 'syncing': return 'Syncing Locations';
            case 'mapping': return 'Map New Locations';
            case 'credentials': return 'Optional Credentials';
            case 'supplier-mapping': return 'Map Supplier Network';
            case 'server-setup': return 'Server Setup';
        }
    };

    return (
        <>
            {canAddLocation && locations.length > 0 && (
                <SettingsPageActions>
                    <Button
                        size="sm"
                        variant="solid"
                        leftIcon={<YaxleIcon name="plus" size="sm" />}
                        onClick={handleOpenAddModal}
                    >
                        Add Location
                    </Button>
                </SettingsPageActions>
            )}

            {locations.length === 0 ? (
                <SettingsEmptyState
                    icon="mapPin"
                    title="No locations configured yet"
                    description={
                        canAddLocation
                            ? 'Add your first location to assign invoices and defaults per shop'
                            : 'Contact us to load your locations into the system'
                    }
                    action={
                        canAddLocation
                            ? { label: 'Add Location', onClick: handleOpenAddModal }
                            : undefined
                    }
                />
            ) : (
                <>
                    <HStack
                        justify="space-between"
                        align="center"
                        gap={4}
                        px={SETTINGS_TABLE_GUTTER}
                        pb={4}
                        flexShrink={0}
                    >
                        <InputGroup size="sm" maxW="500px">
                            <InputLeftElement>
                                <Icon as={TbSearch as React.ElementType} color="gray.400" />
                            </InputLeftElement>
                            <Input
                                placeholder="Search by location name..."
                                value={searchTerm}
                                onChange={(e) => handleSearchChange(e.target.value)}
                                bg="white"
                            />
                        </InputGroup>
                    </HStack>

                    <SettingsTable
                        scrollRef={tableScrollRef}
                        head={
                            <>
                                <SettingsTh
                                    color={nameSortDirection ? "#005BC4" : undefined}
                                    cursor="pointer"
                                    onClick={handleNameSortToggle}
                                    _hover={{ color: "#005BC4" }}
                                    userSelect="none"
                                    transition="color 0.2s"
                                >
                                    <Flex align="center" gap="6px">
                                        NAME
                                        <Box lineHeight={0} color={nameSortDirection ? "#005BC4" : "gray.400"}>
                                            <SortArrowIcon direction={getNameSortDirection()} size={12} />
                                        </Box>
                                    </Flex>
                                </SettingsTh>
                                <SettingsTh>STATUS</SettingsTh>
                                <SettingsTh>CITY</SettingsTh>
                                <SettingsTh>STATE</SettingsTh>
                            </>
                        }
                    >
                        {currentLocations.map((location) => (
                            <Tr key={location._id} _hover={{ bg: "gray.25" }}>
                                <SettingsTd>
                                    <Text fontWeight="semibold" fontSize="sm" color="gray.800">
                                        {location.title}
                                    </Text>
                                </SettingsTd>
                                <SettingsTd>
                                    <Badge
                                        colorScheme={location.subscriptionActive ? "green" : "gray"}
                                        variant="subtle"
                                    >
                                        {location.subscriptionActive ? 'Active' : 'Inactive'}
                                    </Badge>
                                </SettingsTd>
                                <SettingsTd>{location.city}</SettingsTd>
                                <SettingsTd>{location.state}</SettingsTd>
                            </Tr>
                        ))}
                    </SettingsTable>

                    <SettingsTableFooter>
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={setCurrentPage}
                            py={0}
                        />
                    </SettingsTableFooter>
                </>
            )}

            <Modal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                isCentered
                size={getModalSize()}
            >
                <ModalOverlay />
                <ModalContent
                    maxH="85vh"
                    display="flex"
                    flexDirection="column"
                >
                    <ModalHeader
                        bg="gray.50"
                        borderTopRadius="md"
                        borderBottomWidth="1px"
                        borderColor="gray.300"
                        fontSize="md"
                        fontWeight="semibold"
                        px={6}
                        py={4}
                    >
                        {getModalTitle()}
                    </ModalHeader>
                    <ModalCloseButton top={3} />
                    <ModalBody flex={1} minH={0} overflow="hidden" p={0}>
                        {modalStep === 'manual' && (
                            <VStack spacing={4} align="stretch" px={6} pt={3} pb={4}>
                                <FormControl>
                                    <FormLabel fontSize="sm">Location Name</FormLabel>
                                    <Input
                                        placeholder="Downtown Collision Center"
                                        value={manualLocationForm.title}
                                        onChange={(e) => handleManualLocationChange('title', e.target.value)}
                                    />
                                </FormControl>
                                <FormControl isRequired>
                                    <FormLabel fontSize="sm">Address</FormLabel>
                                    <Input
                                        placeholder="123 Main St"
                                        value={manualLocationForm.address}
                                        onChange={(e) => handleManualLocationChange('address', e.target.value)}
                                    />
                                </FormControl>
                                <HStack spacing={3} align="flex-start">
                                    <FormControl isRequired>
                                        <FormLabel fontSize="sm">City</FormLabel>
                                        <Input
                                            placeholder="Austin"
                                            value={manualLocationForm.city}
                                            onChange={(e) => handleManualLocationChange('city', e.target.value)}
                                        />
                                    </FormControl>
                                    <FormControl isRequired maxW="110px">
                                        <FormLabel fontSize="sm">State</FormLabel>
                                        <Input
                                            placeholder="TX"
                                            value={manualLocationForm.state}
                                            onChange={(e) => handleManualLocationChange('state', e.target.value)}
                                        />
                                    </FormControl>
                                    <FormControl isRequired maxW="140px">
                                        <FormLabel fontSize="sm">ZIP</FormLabel>
                                        <Input
                                            placeholder="78701"
                                            value={manualLocationForm.zip}
                                            onChange={(e) => handleManualLocationChange('zip', e.target.value)}
                                        />
                                    </FormControl>
                                </HStack>
                            </VStack>
                        )}
                        {modalStep === 'syncing' && (
                            resync.hasFailed ? (
                                <VStack spacing={4} py={10} px={6}>
                                    <Alert status="error" borderRadius="md">
                                        <AlertIcon />
                                        <AlertDescription fontSize="sm">
                                            {resync.ptError || resync.cccError || 'Location pull failed. Please check credentials.'}
                                        </AlertDescription>
                                    </Alert>
                                </VStack>
                            ) : (
                                <Flex align="center" justify="center" minH="300px">
                                    <AgenticStepLoader
                                        title="Syncing Locations"
                                        subtitle="Pulling locations from PartsTrader and CCC, then filtering out existing ones."
                                        steps={syncAgenticSteps}
                                        currentStep={getSyncCurrentStep()}
                                    />
                                </Flex>
                            )
                        )}
                        {modalStep === 'mapping' && (
                            <Box
                                px={6}
                                pb={4}
                                h="100%"
                                display="flex"
                                flexDirection="column"
                            >
                                <LocationMappingUI
                                    partstraderLocations={ptLocations}
                                    cccLocations={cccLocations}
                                    suggestedMappings={suggestedMappings}
                                    cccMappings={cccMappings}
                                    onToggleCcc={handleToggleCcc}
                                    onAddPt={handleAddPt}
                                    onRemovePt={handleRemovePt}
                                    onConfirm={handleConfirmMapping}
                                    isConfirmLoading={addFromMapping.isPending}
                                    onBack={handleCloseModal}
                                />
                            </Box>
                        )}
                        {modalStep === 'credentials' && (
                            <VStack spacing={4} align="stretch" px={6} py={4} overflowY="auto">
                                <Text fontSize="sm" color="gray.600">
                                    Add Mitchell or OPSTrax credentials for your new locations. These are optional and can be configured later in Integrations.
                                </Text>

                                <Box overflowX="auto">
                                    <Table size="sm" variant="simple">
                                        <Thead>
                                            <Tr>
                                                <Th fontSize="xs">Location</Th>
                                                <Th fontSize="xs">
                                                    <HStack spacing={1}>
                                                        <SourceBadge source="mitchell" />
                                                        <Text>Username</Text>
                                                    </HStack>
                                                </Th>
                                                <Th fontSize="xs">
                                                    <HStack spacing={1}>
                                                        <SourceBadge source="mitchell" />
                                                        <Text>Password</Text>
                                                    </HStack>
                                                </Th>
                                                <Th fontSize="xs">
                                                    <HStack spacing={1}>
                                                        <SourceBadge source="opstrax" />
                                                        <Text>Username</Text>
                                                    </HStack>
                                                </Th>
                                                <Th fontSize="xs">
                                                    <HStack spacing={1}>
                                                        <SourceBadge source="opstrax" />
                                                        <Text>Password</Text>
                                                    </HStack>
                                                </Th>
                                            </Tr>
                                        </Thead>
                                        <Tbody>
                                            {credRows.map((row, idx) => (
                                                <Tr key={row.locationId}>
                                                    <Td>
                                                        <Text fontSize="sm" fontWeight="medium" color="gray.700">
                                                            {row.locationTitle}
                                                        </Text>
                                                    </Td>
                                                    <Td>
                                                        <Input
                                                            size="sm"
                                                            placeholder="Username"
                                                            value={row.mitchellUsername}
                                                            onChange={(e) => updateCredRow(idx, 'mitchellUsername', e.target.value)}
                                                        />
                                                    </Td>
                                                    <Td>
                                                        <InputGroup size="sm">
                                                            <Input
                                                                type={showPasswords[`m-${idx}`] ? 'text' : 'password'}
                                                                placeholder="Password"
                                                                value={row.mitchellPassword}
                                                                onChange={(e) => updateCredRow(idx, 'mitchellPassword', e.target.value)}
                                                            />
                                                            <InputRightElement>
                                                                <IconButton
                                                                    aria-label="Toggle password"
                                                                    icon={<Icon as={(showPasswords[`m-${idx}`] ? FaEyeSlash : FaEye) as React.ElementType} />}
                                                                    size="xs"
                                                                    variant="ghost"
                                                                    onClick={() => togglePassword(`m-${idx}`)}
                                                                />
                                                            </InputRightElement>
                                                        </InputGroup>
                                                    </Td>
                                                    <Td>
                                                        <Input
                                                            size="sm"
                                                            placeholder="Username"
                                                            value={row.opsUsername}
                                                            onChange={(e) => updateCredRow(idx, 'opsUsername', e.target.value)}
                                                        />
                                                    </Td>
                                                    <Td>
                                                        <InputGroup size="sm">
                                                            <Input
                                                                type={showPasswords[`o-${idx}`] ? 'text' : 'password'}
                                                                placeholder="Password"
                                                                value={row.opsPassword}
                                                                onChange={(e) => updateCredRow(idx, 'opsPassword', e.target.value)}
                                                            />
                                                            <InputRightElement>
                                                                <IconButton
                                                                    aria-label="Toggle password"
                                                                    icon={<Icon as={(showPasswords[`o-${idx}`] ? FaEyeSlash : FaEye) as React.ElementType} />}
                                                                    size="xs"
                                                                    variant="ghost"
                                                                    onClick={() => togglePassword(`o-${idx}`)}
                                                                />
                                                            </InputRightElement>
                                                        </InputGroup>
                                                    </Td>
                                                </Tr>
                                            ))}
                                        </Tbody>
                                    </Table>
                                </Box>
                            </VStack>
                        )}
                        {modalStep === 'supplier-mapping' && sessionContext?.organizationId && (
                            <Box px={6} pb={4} h="100%" display="flex" flexDirection="column">
                                <SupplierNetworkStep
                                    organizationId={sessionContext.organizationId}
                                    onComplete={advanceFromSupplierMapping}
                                    onSkip={advanceFromSupplierMapping}
                                    usesPt={resync.resyncData?.workflows.includes('partstrader') ?? true}
                                />
                            </Box>
                        )}
                        {modalStep === 'server-setup' && (
                            <VStack spacing={5} align="stretch" px={6} py={6}>
                                <Text fontSize="sm" color="gray.600">
                                    Your organization uses multi-server mode. Would you like to generate server accounts for the {newLocations.length} new location{newLocations.length !== 1 ? 's' : ''}?
                                </Text>

                                <Box
                                    bg="gray.50"
                                    borderRadius="md"
                                    p={4}
                                    borderWidth={1}
                                    borderColor="gray.200"
                                >
                                    <Text fontSize="sm" fontWeight="semibold" mb={2} color="gray.700">
                                        New locations
                                    </Text>
                                    <VStack spacing={1} align="stretch">
                                        {newLocations.map(loc => (
                                            <Text key={loc._id} fontSize="sm" color="gray.600" pl={2}>
                                                {loc.title}
                                            </Text>
                                        ))}
                                    </VStack>
                                </Box>

                                <Text fontSize="xs" color="gray.400">
                                    This will create desktop app login credentials for each new location. You can also do this later in the IT Setup section.
                                </Text>
                            </VStack>
                        )}
                    </ModalBody>

                    {modalStep === 'manual' && (
                        <>
                            <Divider />
                            <ModalFooter>
                                <HStack spacing={3}>
                                    <Button
                                        size="action"
                                        variant="outlineGray"
                                        onClick={handleCloseModal}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        size="action"
                                        bg={PARTBAY_BLUE}
                                        color="white"
                                        _hover={{ bg: '#005BC4' }}
                                        isLoading={createSingleLocation.isPending}
                                        onClick={handleSubmitManualLocation}
                                    >
                                        Create Location
                                    </Button>
                                </HStack>
                            </ModalFooter>
                        </>
                    )}

                    {modalStep === 'credentials' && (
                        <>
                            <Divider />
                            <ModalFooter>
                                <HStack spacing={3}>
                                    <Button
                                        size="action"
                                        variant="ghost"
                                        onClick={advanceFromCredentials}
                                    >
                                        Skip
                                    </Button>
                                    <Button
                                        size="action"
                                        bg={PARTBAY_BLUE}
                                        color="white"
                                        _hover={{ bg: '#005BC4' }}
                                        leftIcon={<Icon as={FaSave as React.ElementType} />}
                                        isLoading={savingCreds}
                                        onClick={handleSaveCredentials}
                                    >
                                        Save & Continue
                                    </Button>
                                </HStack>
                            </ModalFooter>
                        </>
                    )}

                    {modalStep === 'server-setup' && (
                        <>
                            <Divider />
                            <ModalFooter>
                                <HStack spacing={3}>
                                    <Button
                                        size="action"
                                        variant="ghost"
                                        onClick={handleFinish}
                                    >
                                        Skip
                                    </Button>
                                    <Button
                                        size="action"
                                        bg={PARTBAY_BLUE}
                                        color="white"
                                        _hover={{ bg: '#005BC4' }}
                                        isLoading={generatingAccounts}
                                        loadingText="Generating..."
                                        onClick={handleGenerateServerAccounts}
                                    >
                                        Generate Server Accounts
                                    </Button>
                                </HStack>
                            </ModalFooter>
                        </>
                    )}
                </ModalContent>
            </Modal>
        </>
    );
};

export default Locations;
